import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { useAuthStore } from "@/stores/authStore";
import { setupCache } from "axios-cache-interceptor";
import CryptoHelper from "../helpers/CryptoHelper";
import { useLoginStore } from "@/providers/login/login.provider";
import Cookies from "js-cookie";
class ApiService {
  private axiosInstance: AxiosInstance;
  private cachedAxiosInstance: AxiosInstance;
  private cryptoHelper: CryptoHelper | null;

  constructor(baseURL: string) {
    this.cryptoHelper = process.env.CRYPTO_JS_KEY
      ? new CryptoHelper(process.env.CRYPTO_JS_KEY)
      : null;
    this.axiosInstance = axios.create({ baseURL, timeout: 15000 });
    this.cachedAxiosInstance = setupCache(
      axios.create({ baseURL, timeout: 10000 }),
      {
        ttl: 1000 * 60 * 5, // ⏳ Cache for 5 minutes
        interpretHeader: false, // Ignore server cache headers
        cachePredicate: { statusCheck: (status) => status === 200 }, // Cache only 200 responses
        methods: ["get", "post"],
      }
    );
  }

  private getAxiosInstance(useCache: boolean): AxiosInstance {
    return useCache ? this.cachedAxiosInstance : this.axiosInstance;
  }

  private getHeaders(authRequired: boolean): Record<string, string> {
    const token = useAuthStore.getState()?.token ?? Cookies.get("ACCESS_TOKEN");
    // const token = useLoginStore((state) => state.token);

    return {
      "Content-Type": "application/json",
      "x-access-token": "", //hack for staging
      ...(authRequired && token ? { "x-access-token": token } : {}),
    };
  }

  private encryptData(data: any): string {
    if (!this.cryptoHelper) {
      throw new Error("Encryption key is not set");
    }
    return this.cryptoHelper.encrypt(data);
  }

  private decryptData<T>(data: string): T {
    if (!this.cryptoHelper) {
      throw new Error("Encryption key is not set");
    }
    return this.cryptoHelper.decrypt<T>(data);
  }

  public async get<T>(
    url: string,
    params?: Record<string, any>,
    authRequired: boolean = false,
    useCache: boolean = false
  ): Promise<T> {
    try {
      const axiosInstance = this.getAxiosInstance(useCache);
      const headers = this.getHeaders(authRequired);

      // console.log("📌 GET Request:", { url, params, headers });

      const response: AxiosResponse<T> = await axiosInstance.get(url, {
        params,
        headers,
      });

      // console.log("✅ GET Response:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ GET Error:", error);
      throw error;
    }
  }

  public async post<T>(
    url: string,
    data?: Record<string, any>,
    authRequired: boolean = false,
    useCache: boolean = false,
    encryptData: boolean = false
  ): Promise<T> {
    try {
      const axiosInstance = this.getAxiosInstance(useCache);
      const headers = this.getHeaders(authRequired);
      const requestData =
        encryptData && data ? { data: this.encryptData(data) } : data;

      // console.log("📌 POST Request:", { url, requestData, headers });
      // console.log(this.decryptData(requestData));
      const response: AxiosResponse<T> = await axiosInstance.post(
        url,
        requestData,
        {
          headers,
        }
      );

      // console.log("✅ POST Response:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ POST Error:", error);
      throw error;
    }
  }

  public async put<T>(
    url: string,
    data?: Record<string, any>,
    authRequired: boolean = false
  ): Promise<T> {
    try {
      const headers = this.getHeaders(authRequired);

      console.log("📌 PUT Request:", { url, data, headers });

      const response: AxiosResponse<T> = await this.axiosInstance.put(
        url,
        data,
        { headers }
      );

      console.log("✅ PUT Response:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ PUT Error:", error);
      throw error;
    }
  }

  public async patch<T>(
    url: string,
    data?: Record<string, any>,
    authRequired: boolean = false
  ): Promise<T> {
    try {
      const headers = this.getHeaders(authRequired);

      console.log("📌 PATCH Request:", { url, data, headers });

      const response: AxiosResponse<T> = await this.axiosInstance.patch(
        url,
        data,
        { headers }
      );

      console.log("✅ PATCH Response:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ PATCH Error:", error);
      throw error;
    }
  }
}

export default ApiService;
